import { useState, useEffect } from 'react';
import { CalendarRange, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { toast } from 'sonner';
import { edicaoApi } from '../../lib/api';
import logger from '../../lib/logger';

const Row = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-gray-300">{label}</Label>
    {children}
    {hint && <p className="text-gray-500 text-xs">{hint}</p>}
  </div>
);
const inputCls = 'bg-obsidian-50 border-white/10 text-white';

export const EdicaoConfigCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    edicaoApi.get().then((r) => setCfg(r.data)).catch((e) => { logger.error(e); toast.error('Erro ao carregar edição'); });
  }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, updated_by, ...payload } = cfg;
      const r = await edicaoApi.update({ ...payload, ano: Number(payload.ano) });
      setCfg(r.data);
      toast.success('Edição atualizada. O site público já reflete as mudanças.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-obsidian-200 border-white/10" data-testid="edicao-config-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><CalendarRange className="w-5 h-5 text-kairos-orange" />Próxima edição (site público)</CardTitle>
        <CardDescription className="text-gray-400">Datas, local, pré-inscrições e status do primeiro lote exibidos na página inicial.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Row label="Ano"><Input type="number" value={cfg.ano} onChange={(e) => set('ano', e.target.value)} className={inputCls} data-testid="edicao-ano" /></Row>
          <Row label="Data de início" hint="Exibida no site como 12 a 15 de novembro de 2027"><Input type="date" value={cfg.data_inicio} onChange={(e) => set('data_inicio', e.target.value)} className={inputCls} data-testid="edicao-data-inicio" /></Row>
          <Row label="Data de término"><Input type="date" value={cfg.data_fim} onChange={(e) => set('data_fim', e.target.value)} className={inputCls} data-testid="edicao-data-fim" /></Row>
        </div>
        <Row label="Nome da edição"><Input value={cfg.nome} onChange={(e) => set('nome', e.target.value)} className={inputCls} data-testid="edicao-nome" /></Row>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-obsidian-50 border border-white/5">
          <Row label="Local do evento" hint="Enquanto não confirmado, o site mostra 'Local a confirmar'."><Input value={cfg.local_nome} onChange={(e) => set('local_nome', e.target.value)} placeholder="Ex.: Hotel Fazenda ..." className={inputCls} data-testid="edicao-local-nome" /></Row>
          <Row label="Cidade/UF do local"><Input value={cfg.local_cidade} onChange={(e) => set('local_cidade', e.target.value)} placeholder="Ex.: Ji-Paraná/RO" className={inputCls} data-testid="edicao-local-cidade" /></Row>
          <label className="flex items-center gap-3 text-gray-300 text-sm md:col-span-2"><Switch className="data-[state=unchecked]:bg-white/20" checked={!!cfg.local_confirmado} onCheckedChange={(v) => set('local_confirmado', v)} data-testid="edicao-local-confirmado" />Local confirmado (exibir publicamente)</label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 text-gray-300 text-sm p-4 rounded-lg bg-obsidian-50 border border-white/5"><Switch className="data-[state=unchecked]:bg-white/20" checked={!!cfg.pre_inscricoes_abertas} onCheckedChange={(v) => set('pre_inscricoes_abertas', v)} data-testid="edicao-pre-inscricoes" />Pré-inscrições abertas</label>
          <Row label="Status do primeiro lote">
            <select value={cfg.status_lote} onChange={(e) => set('status_lote', e.target.value)} className="w-full h-10 rounded-md bg-obsidian-50 border border-white/10 text-white px-3 text-sm" data-testid="edicao-status-lote">
              <option value="em_breve">Em breve (sem valores)</option>
              <option value="aberto">Primeiro lote aberto</option>
              <option value="encerrado">Encerrado</option>
            </select>
          </Row>
        </div>
        <Row label="Aviso sobre o lote (texto exibido no hero)"><Input value={cfg.lote_mensagem} onChange={(e) => set('lote_mensagem', e.target.value)} className={inputCls} data-testid="edicao-lote-mensagem" /></Row>
        <Row label="WhatsApp de contato (somente números, com 55)"><Input value={cfg.whatsapp_contato} onChange={(e) => set('whatsapp_contato', e.target.value.replace(/\D/g, ''))} className={inputCls} data-testid="edicao-whatsapp" /></Row>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 text-gray-400 text-sm p-4 rounded-lg bg-obsidian-50 border border-white/5"><Switch className="data-[state=unchecked]:bg-white/20" checked={!!cfg.mostrar_palestrantes} onCheckedChange={(v) => set('mostrar_palestrantes', v)} disabled data-testid="edicao-palestrantes" />Palestrantes 2027 (módulo em preparação)</label>
          <label className="flex items-center gap-3 text-gray-400 text-sm p-4 rounded-lg bg-obsidian-50 border border-white/5"><Switch className="data-[state=unchecked]:bg-white/20" checked={!!cfg.mostrar_programacao} onCheckedChange={(v) => set('mostrar_programacao', v)} disabled data-testid="edicao-programacao" />Programação 2027 (módulo em preparação)</label>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-gray-500 text-xs">{cfg.updated_at ? `Atualizado em ${new Date(cfg.updated_at).toLocaleString('pt-BR')} por ${cfg.updated_by}` : 'Usando valores padrão'}</p>
          <Button onClick={save} disabled={saving} className="bg-kairos-orange hover:bg-kairos-orange-dark text-white" data-testid="edicao-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Salvando...' : 'Salvar edição'}</Button>
        </div>
      </CardContent>
    </Card>
  );
};
